import React, { useEffect, useState } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { success, failure } from '~/redux/actions/snackbar-actions';
import { fetchTags, updateTag } from '~/redux/actions/tags-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';

import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import Typography from '@mui/material/Typography';
import { Box, InputLabel, TextField, CircularProgress } from '@mui/material';

// Styles
import './styles.scss';

const EditTagModal = (props) => {
  const formId = 'edit-tag-form';
  const { openModal, handleCloseModal, selectedTag, pagination } = props;

  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .max(35, MESSAGES.SETTINGS.TAGS.EDIT.TAG_LIMIT)
      .required(MESSAGES.SETTINGS.TAGS.EDIT.TAG_REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: validationSchema,
  });

  const onClose = () => {
    handleCloseModal();
    formik.handleReset();
    resetValidation();
  };

  const updateExistingTag = async (event) => {
    try {
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }
      setLoading(true);
      const payload = {
        title: formik.values.name.trim(),
      };
      const response = await dispatch(updateTag(selectedTag.id, payload));
      dispatch(fetchTags(pagination));
      dispatch(success(response.data.message));
      onClose();
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
      return error;
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    formik.values.name = selectedTag.title;
  }, [selectedTag]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.SETTINGS.TAGS.EDIT.TITLE}
      className="editTagModalWrapper"
      contentClassName="exportModalContent"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
            {MESSAGES.SETTINGS.TAGS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton
            variant="primary"
            type="submit"
            form={formId}
            onClick={() => validateForm(formik)}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              MESSAGES.SETTINGS.TAGS.EDIT.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      <form id={formId} className="updateFormWrapper" onSubmit={updateExistingTag} noValidate>
        <Box className="inputWrapper">
          <Typography className="inputLabel formLabel">
            {MESSAGES.SETTINGS.TAGS.EDIT.TAG_LABEL}
          </Typography>
          <TextField
            placeholder={MESSAGES.SETTINGS.TAGS.EDIT.TAG_PLACEHOLDER}
            fullWidth
            name="name"
            autoComplete="off"
            onChange={formik.handleChange}
            className={
              formik.errors.name && formik.touched.name
                ? 'invalid formInput inputField formField'
                : 'inputField formField'
            }
            onBlur={realTimeValidation ? formik.handleBlur : undefined}
            onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
            value={formik.values.name}
            sx={{
              '& input::placeholder': {
                color: '#000',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: 'normal',
                fontFamily: 'Inter',
                fontStyle: 'normal',
              },
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
            }}
          />
          {formik.touched.name && formik.errors.name ? (
            <Typography className="errorMessage">
              <sup>*</sup>
              {formik.errors.name}
            </Typography>
          ) : null}
        </Box>
      </form>
    </OlcFormModal>
  );
};

export default EditTagModal;
